import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmt = v => v != null && !isNaN(v) ? `₹${Math.round(Number(v)).toLocaleString('en-IN')}` : '—';
const pctOf = (val, total) => total > 0 ? (val / total * 100).toFixed(1) : '0.0';

/* Same classification rules as Admin P&L — purely display logic. */
function isOperationalCostKey(key) {
  const k = key.toLowerCase().trim();
  const SYSTEM_EXACT = new Set(['food net revenue', 'liquor & beverage net revenue', 'food sale', 'beverage sale', 'net sales',
    'room net revenue', 'food cost', 'liquor & bev cost', 'beverage cost', 'other purchases',
    'net fnb sale', 'gross revenue', 'total f & b cost', 'gross profit', 'total cost',
    'total expenditure', 'profit & loss', 'net profit', 'total revenue',
    'covers', 'apc', 'total room inventory', 'room occupancy', 'occupancy percentage', 'arr',
    'average room revenue', 'salary & manpower cost', 'operating cost', 'admn & selling cost',
    'marketing and commissions cost', 'other expenses', 'maintenance', 'marketing', 'marketing spend']);
  if (SYSTEM_EXACT.has(k)) return true;
  const OPERATIONAL_PATTERNS = [
    /\belectricity\b/, /\bfuel\b/, /\bgas\b/, /\bcharcoal\b/, /\bdiesel\b/, /\bpetrol\b/,
    /\bwater\b/, /\brepair\b/, /\bmaintenance\b/, /\bcooking gas\b/, /\bwooden fuel\b/,
  ];
  return OPERATIONAL_PATTERNS.some(p => p.test(k));
}

function getBudget(budgetItems, ...names) {
  if (!budgetItems || Object.keys(budgetItems).length === 0) return null;
  for (const name of names) {
    const key = name.toLowerCase().trim();
    if (budgetItems[key]) return budgetItems[key];
  }
  return null;
}

/* Read-only P&L row with Excel-style freeze panes — sticky top header,
   sticky left first column, anchored top-left corner cell. */
const GRID_BUDGET = '220px 12% 12% 12% 12% 8% 9%';
const GRID_NO_BUDGET = '220px 110px 70px';
const MIN_WIDTH_BUDGET = 900;
const MIN_WIDTH_NO_BUDGET = 420;

const STICKY_FIRST_COL_DEFAULT_BG = '#FFFFFF';
const STICKY_FIRST_COL_HIGHLIGHT_BG = '#F9FAFB';
const STICKY_HEADER_BG = '#E7EDE8';
const STICKY_SECTION_BG = '#F3F5F2';

function Row({ label, actual, budget, indent, bold, isProfit, isSubtotal, pctUsed, costPct, highlight, showBudgetCols }) {
  const hasBudget = budget && budget.month_budget > 0;
  const variance = hasBudget ? actual - budget.mtd_target : null;
  const profitColor = isProfit ? (actual >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]') : '';
  const isRevenueLine = label.toLowerCase().includes('revenue') || label.toLowerCase().includes('sale');
  const varianceColor = variance != null ? (
    isProfit || isRevenueLine
      ? (variance >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]')
      : (variance <= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]')
  ) : '';
  const costPctColor = costPct != null ? (
    isProfit ? (costPct >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]')
      : (costPct > 30 ? 'text-[#C45A41]' : costPct > 20 ? 'text-[#D4A017]' : 'text-[#2D6A4F]')
  ) : '';
  const cols = showBudgetCols ? GRID_BUDGET : GRID_NO_BUDGET;
  const rowBg = highlight ? STICKY_FIRST_COL_HIGHLIGHT_BG : STICKY_FIRST_COL_DEFAULT_BG;
  const numCls = 'text-right tabular-nums whitespace-nowrap overflow-hidden text-ellipsis min-w-0';
  const stickyCell = {
    position: 'sticky',
    left: 0,
    zIndex: 10,
    backgroundColor: rowBg,
    boxShadow: 'inset -1px 0 0 0 rgba(226, 232, 223, 0.9)',
    paddingLeft: indent ? 20 : 8,
    paddingRight: 8,
  };

  return (
    <div className={`grid items-center text-[13px] py-2 gap-x-2 ${bold ? 'font-semibold' : ''} ${isSubtotal ? 'border-t border-[#E2E8DF]' : 'border-b border-[#E2E8DF]/40'} ${isProfit ? `${profitColor} font-bold` : ''}`}
      style={{ gridTemplateColumns: cols, backgroundColor: rowBg }}>
      <span
        className={`truncate min-w-0 ${isProfit ? '' : 'text-[color:var(--color-text-muted)]'}`}
        style={stickyCell}
        title={label}>{label}</span>
      <span className={`${numCls} ${isProfit ? '' : 'text-[color:var(--color-text-primary)] font-medium'}`}>{fmt(actual)}</span>
      {showBudgetCols ? (
        <>
          <span className={`${numCls} text-[color:var(--color-text-muted)] text-[12px]`}>{hasBudget ? fmt(budget.month_budget) : ''}</span>
          <span className={`${numCls} text-[color:var(--color-text-muted)] text-[12px]`}>{hasBudget ? fmt(budget.mtd_target) : ''}</span>
          <span className={`${numCls} text-[12px] font-semibold ${varianceColor}`}>
            {variance != null ? `${variance >= 0 ? '+' : ''}${fmt(variance)}` : ''}
          </span>
          <span className={`${numCls} text-[12px] font-semibold ${costPctColor}`}>
            {costPct != null ? `${costPct}%` : ''}
          </span>
          <span className={`${numCls} text-[12px] font-bold text-[color:var(--color-text-primary)] pr-2`}>
            {pctUsed != null ? `${pctUsed}%` : ''}
          </span>
        </>
      ) : (
        <span className={`${numCls} text-[12px] font-semibold ${costPctColor} pr-2`}>
          {costPct != null ? `${costPct}%` : ''}
        </span>
      )}
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <div
      className="text-[11px] font-bold text-[color:var(--color-primary)] uppercase tracking-[0.08em] py-2 border-y-2 border-[color:var(--color-primary)]/20"
      style={{
        position: 'sticky',
        left: 0,
        zIndex: 12,
        backgroundColor: STICKY_SECTION_BG,
        paddingLeft: 8,
        paddingRight: 8,
      }}>
      {label}
    </div>
  );
}

function ColumnHeaders({ hasBudget }) {
  const cols = hasBudget ? GRID_BUDGET : GRID_NO_BUDGET;
  return (
    <div
      className="grid items-center text-[10px] uppercase tracking-wider text-[color:var(--color-text-primary)] font-bold py-2.5 gap-x-2 border-b-2 border-[color:var(--color-primary)]/30 shadow-sm"
      style={{
        gridTemplateColumns: cols,
        position: 'sticky',
        top: 0,
        zIndex: 20,
        backgroundColor: STICKY_HEADER_BG,
      }}>
      <span
        style={{
          position: 'sticky',
          left: 0,
          zIndex: 25,
          backgroundColor: STICKY_HEADER_BG,
          paddingLeft: 8,
          paddingRight: 8,
          boxShadow: 'inset -1px 0 0 0 rgba(20, 61, 48, 0.15)',
        }}>Line Item</span>
      <span className="text-right">Actual MTD</span>
      {hasBudget ? (
        <>
          <span className="text-right">Month Bgt</span>
          <span className="text-right">MTD Target</span>
          <span className="text-right">Variance</span>
          <span className="text-right">Cost %</span>
          <span className="text-right pr-2">% Used</span>
        </>
      ) : (
        <span className="text-right pr-2">Cost %</span>
      )}
    </div>
  );
}

function OutletStatement({ o, pnlData }) {
  const sys = o.system || {};
  const bl = o.budget_line_items || {};
  const hasBgt = Object.keys(bl).length > 0;
  const totalRevenue = o.total_revenue || sys.total_revenue || 0;
  const foodCost = o.food_cost || sys.food_cost || 0;
  const bevCost = o.beverage_cost || sys.beverage_cost || 0;
  const totalCogs = foodCost + bevCost;
  const grossProfit = totalRevenue - totalCogs;
  const localPctUsed = o.pct_used || {};
  const inactiveKeys = new Set((o.custom_line_items || []).filter(i => !i.active).map(i => i.name.toLowerCase().trim()));

  const getSystemActualForKey = (key) => {
    const k = key.toLowerCase().trim();
    if (/\belectricity\b/.test(k)) return sys.electricity_cost || 0;
    if (/\bfuel\b|\bgas\b|\bcharcoal\b|\bdiesel\b|\bpetrol\b|\bwooden\b/.test(k)) return sys.gas_cost || 0;
    if (/\bwater\b/.test(k)) return sys.water_cost || 0;
    if (/\brepair\b|\bmaintenance\b/.test(k)) return sys.repairs_cost || (o.maintenance_cost || sys.maintenance_cost || 0);
    if (/\bmarketing\b/.test(k)) return o.marketing_spend_mtd || 0;
    if (/\bother purchas|\bstore issu/.test(k)) return o.other_purchases || sys.other_purchases || 0;
    return 0;
  };

  const CALCULATED_KEYS = new Set(['net fnb sale', 'net  fnb sale', 'gross revenue', 'total f & b cost',
    'total  f & b cost', 'gross profit', 'total cost', 'total expenditure', 'profit & loss', 'net profit',
    'total revenue', 'salary & manpower cost', 'operating cost', 'admn & selling cost',
    'other expenses', 'total cost of sales']);
  const REVENUE_KEYS = new Set(['food net revenue', 'food sale', 'food revenue',
    'liquor & beverage net revenue', 'beverage sale', 'beverage revenue', 'bev sale',
    'net sales', 'room net revenue', 'room revenue', 'other sale']);
  const COGS_KEYS = new Set(['food cost', 'liquor & bev cost', 'beverage cost', 'bev cost']);
  const METRIC_KEYS = new Set(['covers', 'apc', 'total room inventory', 'room occupancy',
    'occupancy percentage', 'arr', 'arr (average room revenue)', 'average room revenue']);
  const SKIP_KEYS = new Set([...CALCULATED_KEYS, ...REVENUE_KEYS, ...COGS_KEYS, ...METRIC_KEYS]);

  const opExpItems = [];
  const fixedItems = [];
  let totalOpExp = 0;
  let totalFixedActual = 0;
  let totalFixedBudget = 0;
  let totalFixedMtdTarget = 0;

  // Used only by the legacy fallback classifier below.
  const seenOpPatterns = new Set();
  const tagPattern = (k) => {
    if (/\belectricity\b/.test(k)) seenOpPatterns.add('electricity');
    if (/\bfuel\b|\bgas\b|\bcharcoal\b|\bdiesel\b|\bpetrol\b|\bwooden\b/.test(k)) seenOpPatterns.add('gas');
    if (/\bwater\b/.test(k)) seenOpPatterns.add('water');
    if (/\brepair\b|\bmaintenance\b/.test(k)) seenOpPatterns.add('repairs');
    if (/\bmarketing\b/.test(k)) seenOpPatterns.add('marketing');
    if (/\bother purchas|\bstore issu/.test(k)) seenOpPatterns.add('other_purchases');
  };

  // ── ITER249 — Prefer backend `_statement` (single source of truth
  // shared with AdminPnL + Review & Compare). Falls back to legacy
  // client-side classifier only if the backend response is missing
  // `_statement` (older cache / older backend). This eliminates the
  // ManagerPnL-vs-AdminPnL subtotal drift caused by two different
  // classifiers.
  const stmt = o._statement || null;
  const useBackendStatement = stmt
    && Array.isArray(stmt.op_ex_items) && Array.isArray(stmt.fixed_items);

  if (useBackendStatement) {
    stmt.op_ex_items.forEach(it => {
      if (inactiveKeys.has(String(it.label || it.key).toLowerCase().trim())) return;
      // Match legacy shape so <Row> renders identically. Budget lookup
      // uses the original `bl` map (keyed by lower-case label).
      const legacyKey = String(it.key || '').toLowerCase().trim();
      const budget = bl[legacyKey] || null;
      opExpItems.push({
        key: it.key, label: it.label, actual: it.actual || 0, budget,
      });
      totalOpExp += (it.actual || 0);
    });
    stmt.fixed_items.forEach(it => {
      if (inactiveKeys.has(String(it.label || it.key).toLowerCase().trim())) return;
      const legacyKey = String(it.key || '').toLowerCase().trim();
      const budget = bl[legacyKey] || null;
      const pct = (it.pct_used != null)
        ? parseFloat(it.pct_used)
        : parseFloat(localPctUsed[legacyKey] ?? 100);
      fixedItems.push({
        key: it.key, label: it.label,
        actual: it.actual || 0, budget, pct_used: pct,
      });
      totalFixedActual   += (it.actual || 0);
      totalFixedBudget   += budget?.month_budget || 0;
      totalFixedMtdTarget += budget?.mtd_target || 0;
    });
  } else if (hasBgt) {
    Object.entries(bl).forEach(([key, bval]) => {
      if (SKIP_KEYS.has(key) || inactiveKeys.has(key)) return;
      const label = key.replace(/\b\w/g, c => c.toUpperCase());
      if (isOperationalCostKey(key)) {
        const actual = getSystemActualForKey(key);
        opExpItems.push({ key, label, actual, budget: bval });
        totalOpExp += actual;
        tagPattern(key.toLowerCase().trim());
      } else {
        const pct = parseFloat(localPctUsed[key] ?? 100);
        const actualMtd = Math.round((bval.mtd_target || 0) * (pct / 100));
        fixedItems.push({ key, label, actual: actualMtd, budget: bval, pct_used: pct });
        totalFixedActual += actualMtd;
        totalFixedBudget += bval.month_budget || 0;
        totalFixedMtdTarget += bval.mtd_target || 0;
      }
    });
  }

  const SUPPLEMENTAL_OP = [
    { key: '_sup_other_purchases', label: 'Other Purchases / Store Issues', actual: o.other_purchases || sys.other_purchases || 0, pattern: 'other_purchases' },
    { key: '_sup_electricity', label: 'Electricity', actual: sys.electricity_cost || 0, pattern: 'electricity' },
    { key: '_sup_gas', label: 'Fuel / Gas', actual: sys.gas_cost || 0, pattern: 'gas' },
    { key: '_sup_water', label: 'Water', actual: sys.water_cost || 0, pattern: 'water' },
    { key: '_sup_repairs', label: 'Maintenance & Repairs', actual: sys.repairs_cost || 0, pattern: 'repairs' },
    { key: '_sup_marketing', label: 'Marketing', actual: o.marketing_spend_mtd || 0, pattern: 'marketing' },
  ];
  // Supplemental rows are ONLY needed on the legacy fallback path — the
  // backend `_statement` already emits them (see pnl_statement_rows.py).
  if (!useBackendStatement) {
    SUPPLEMENTAL_OP.forEach(sup => {
      if (seenOpPatterns.has(sup.pattern)) return;
      if ((sup.actual || 0) <= 0) return;
      opExpItems.push({ key: sup.key, label: sup.label, actual: sup.actual, budget: null });
      totalOpExp += sup.actual;
    });
  }

  // ── ITER249 — Prefer backend-reconciled subtotals when available.
  // These are computed by services/pnl_statement_rows::reconcile_statement_totals
  // and shared identically with Admin P&L and Review & Compare.
  const totalExpenditure = (o.total_expenditure != null)
    ? o.total_expenditure
    : (totalCogs + totalOpExp + totalFixedActual);
  const netProfit = (o.net_profit != null && useBackendStatement)
    ? o.net_profit
    : (totalRevenue - totalExpenditure);

  return (
    <>
    <div
      className="-mx-2 border border-[#E2E8DF] rounded-md bg-white"
      style={{ overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}
      data-testid={`manager-pnl-statement-${o.outlet_id}`}>
      <div className="space-y-0" style={{ minWidth: hasBgt ? `${MIN_WIDTH_BUDGET}px` : `${MIN_WIDTH_NO_BUDGET}px`, width: 'max-content' }}>
      <ColumnHeaders hasBudget={hasBgt} />

      <SectionHeader label="Revenue" />
      <Row label="Food Net Revenue" actual={o.food_sale || sys.food_sale || 0}
        budget={getBudget(bl, 'food net revenue', 'food sale', 'food revenue')} indent showBudgetCols={hasBgt} />
      <Row label="Beverage Net Revenue" actual={o.beverage_sale || sys.beverage_sale || 0}
        budget={getBudget(bl, 'liquor & beverage net revenue', 'beverage sale', 'beverage revenue', 'bev sale')} indent showBudgetCols={hasBgt} />
      {(o.rooms_enabled && o.room_revenue > 0) && (
        <Row label="Room Revenue" actual={o.room_revenue}
          budget={getBudget(bl, 'room net revenue', 'room revenue')} indent showBudgetCols={hasBgt} />
      )}
      <Row label="Total Revenue" actual={totalRevenue}
        budget={getBudget(bl, 'gross revenue', 'total revenue', 'net sales')} bold isSubtotal highlight showBudgetCols={hasBgt} />

      <SectionHeader label="Cost of Goods Sold" />
      <Row label="Food Cost" actual={foodCost}
        budget={getBudget(bl, 'food cost')} indent showBudgetCols={hasBgt}
        costPct={totalRevenue > 0 ? parseFloat(pctOf(foodCost, totalRevenue)) : null} />
      <Row label="Beverage Cost" actual={bevCost}
        budget={getBudget(bl, 'liquor & bev cost', 'beverage cost', 'bev cost')} indent showBudgetCols={hasBgt}
        costPct={totalRevenue > 0 ? parseFloat(pctOf(bevCost, totalRevenue)) : null} />
      <Row label="Total COGS" actual={totalCogs}
        budget={getBudget(bl, 'total f & b cost', 'total cost of sales')} bold isSubtotal showBudgetCols={hasBgt}
        costPct={totalRevenue > 0 ? parseFloat(pctOf(totalCogs, totalRevenue)) : null} />

      <Row label="Gross Profit" actual={grossProfit}
        budget={getBudget(bl, 'gross profit')} bold isProfit isSubtotal highlight showBudgetCols={hasBgt}
        costPct={totalRevenue > 0 ? parseFloat(pctOf(grossProfit, totalRevenue)) : null} />

      {opExpItems.length > 0 && (
        <>
          <SectionHeader label="Operating Expenses" />
          {opExpItems.map(item => (
            <Row key={item.key} label={item.label} actual={item.actual}
              budget={item.budget} indent showBudgetCols={hasBgt}
              costPct={totalRevenue > 0 ? parseFloat(pctOf(item.actual, totalRevenue)) : null} />
          ))}
          <Row label="Total Operating Expenses" actual={totalOpExp}
            bold isSubtotal showBudgetCols={hasBgt}
            costPct={totalRevenue > 0 ? parseFloat(pctOf(totalOpExp, totalRevenue)) : null} />
        </>
      )}

      {fixedItems.length > 0 && (
        <>
          <SectionHeader label="Fixed Costs" />
          {fixedItems.map(item => (
            <Row key={item.key} label={item.label} actual={item.actual}
              budget={item.budget} indent pctUsed={item.pct_used} showBudgetCols={hasBgt}
              costPct={totalRevenue > 0 ? parseFloat(pctOf(item.actual, totalRevenue)) : null} />
          ))}
          <Row label="Total Fixed Costs" actual={totalFixedActual}
            budget={hasBgt ? { month_budget: totalFixedBudget, mtd_target: totalFixedMtdTarget } : null}
            bold isSubtotal showBudgetCols={hasBgt}
            costPct={totalRevenue > 0 ? parseFloat(pctOf(totalFixedActual, totalRevenue)) : null} />
        </>
      )}

      <Row label="Total Expenditure" actual={totalExpenditure} bold isSubtotal showBudgetCols={hasBgt}
        budget={getBudget(bl, 'total expenditure')}
        costPct={totalRevenue > 0 ? parseFloat(pctOf(totalExpenditure, totalRevenue)) : null} />

      <div className="border-t-2 border-[color:var(--color-primary)] mt-1 pt-1 bg-[#F9FAFB] rounded-b" data-testid={`manager-pnl-net-${o.outlet_id}`}>
        <Row label="Net Profit / (Loss)" actual={netProfit}
          budget={getBudget(bl, 'profit & loss', 'net profit', 'profit and loss')} bold isProfit showBudgetCols={hasBgt}
          costPct={totalRevenue > 0 ? parseFloat(pctOf(netProfit, totalRevenue)) : null} />
      </div>
      </div>
    </div>

      {!hasBgt && (
        <div className="mt-3 px-3 py-2 bg-[#F9F9F8] rounded-lg border border-[#E2E8DF]">
          <p className="text-[10px] text-[color:var(--color-text-muted)] text-center">
            No budget has been uploaded for this outlet yet. Ask your admin to upload the yearly P&L budget to see budget comparison columns.
          </p>
        </div>
      )}

      <div className="mt-3 px-2 py-2 bg-[#F9F9F8] rounded-lg border border-[#E2E8DF]" data-testid={`manager-mtd-${o.outlet_id}`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-semibold text-[color:var(--color-text-muted)] uppercase tracking-wide">MTD Progress</span>
          <span className="text-xs font-bold text-[color:var(--color-text-primary)]">{pnlData?.elapsed_days || 0} of {pnlData?.days_in_month || 30} days</span>
        </div>
        <div className="w-full bg-[#E2E8DF] rounded-full h-1.5">
          <div className="bg-[#2D6A4F] h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, ((pnlData?.elapsed_days || 0) / (pnlData?.days_in_month || 30)) * 100)}%` }} />
        </div>
        <p className="text-[9px] text-[color:var(--color-text-muted)] mt-0.5">MTD Target based on calendar days elapsed (excluding today) · {o.days_reported} days with data entries</p>
      </div>
    </>
  );
}

export default function ManagerPnL() {
  const { getHeaders } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [pnlData, setPnlData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchPnL = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/manager/pnl`, {
        params: { year, month },
        headers: getHeaders(),
      });
      setPnlData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load P&L');
      setPnlData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, getHeaders]);

  useEffect(() => { fetchPnL(); }, [fetchPnL]);

  const outlet = pnlData?.outlets?.[0];

  return (
    <div className="min-h-screen bg-[#F9F9F8] pb-10">
      <div className="max-w-5xl mx-auto px-4 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-[color:var(--color-text-primary)] tracking-tight flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-[color:var(--color-primary)]" /> Profit & Loss
            </h1>
            <p className="text-xs text-[color:var(--color-text-muted)] mt-0.5">
              {outlet?.outlet_name ? `${outlet.outlet_name} · ` : ''}
              MTD performance · read-only
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(month)} onValueChange={v => setMonth(parseInt(v))}>
              <SelectTrigger className="h-9 w-24 text-xs" data-testid="manager-pnl-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={v => setYear(parseInt(v))}>
              <SelectTrigger className="h-9 w-24 text-xs" data-testid="manager-pnl-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[today.getFullYear() - 1, today.getFullYear()].map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mb-3 px-3 py-2 bg-[#EDF4EE] border border-[#2D6A4F]/20 rounded-lg flex items-start gap-2" data-testid="manager-pnl-readonly-banner">
          <Info className="w-4 h-4 text-[#2D6A4F] shrink-0 mt-0.5" />
          <p className="text-[11px] text-[color:var(--color-primary)] leading-snug">
            This is a <strong>read-only</strong> P&L view. Budgets, % Used and finalization are managed by your admin.
            Numbers match the Admin P&L for your outlet exactly.
          </p>
        </div>

        {loading && (
          <Card className="border-[#E2E8DF]" data-testid="manager-pnl-loading">
            <CardContent className="py-8 text-center text-xs text-[color:var(--color-text-muted)]">Loading P&L…</CardContent>
          </Card>
        )}

        {!loading && outlet && (
          <Card className="border-[#E2E8DF] shadow-sm overflow-hidden" data-testid={`manager-pnl-card-${outlet.outlet_id}`}>
            <CardHeader className="py-3 px-4 border-b border-[#E2E8DF] bg-white">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-[color:var(--color-text-primary)] flex items-center gap-2">
                  {outlet.outlet_name}
                  <span className="text-[10px] font-normal text-[color:var(--color-text-muted)]">
                    · Through {pnlData.data_through?.slice(0, 10)}
                  </span>
                </CardTitle>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="text-[color:var(--color-text-muted)]">
                    Sales: <span className="font-semibold text-[color:var(--color-text-primary)]">{fmt(outlet.total_sales)}</span>
                  </span>
                  <span className={`flex items-center gap-1 font-semibold ${outlet.net_profit >= 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                    <TrendingUp className="w-3 h-3" />
                    NP: {fmt(outlet.net_profit)} ({outlet.net_margin_pct}%)
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-2">
              <OutletStatement o={outlet} pnlData={pnlData} />
            </CardContent>
          </Card>
        )}

        {!loading && !outlet && (
          <Card className="border-[#E2E8DF]" data-testid="manager-pnl-empty">
            <CardContent className="py-8 text-center text-xs text-[color:var(--color-text-muted)]">
              No P&L data available for this period.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
